import * as React from 'react';
import { Shield, FileText, DollarSign, Calendar, User } from 'lucide-react';
import { Table, SortDirection } from '@components/base/data';
import { Text, Badge, Icon } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { Button } from '@components/base/actions';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// Sample data for policies
interface PolicyData {
  id: string;
  policyNumber: string;
  holder: string;
  type: string;
  premium: string;
  status: 'active' | 'pending' | 'expired';
  startDate: string;
}

const samplePolicies: PolicyData[] = [
  { id: '1', policyNumber: 'POL-2024-001', holder: 'Acme Corp', type: 'Commercial', premium: '$12,450', status: 'active', startDate: '2024-01-15' },
  { id: '2', policyNumber: 'POL-2024-002', holder: 'Tech Solutions LLC', type: 'Cyber Liability', premium: '$8,200', status: 'active', startDate: '2024-02-01' },
  { id: '3', policyNumber: 'POL-2024-003', holder: 'Global Industries', type: 'Workers Comp', premium: '$25,800', status: 'pending', startDate: '2024-03-01' },
  { id: '4', policyNumber: 'POL-2023-089', holder: 'Smith & Associates', type: 'Professional', premium: '$5,600', status: 'expired', startDate: '2023-04-01' },
  { id: '5', policyNumber: 'POL-2024-004', holder: 'Metro Healthcare', type: 'Medical Malpractice', premium: '$45,000', status: 'active', startDate: '2024-01-01' },
];

const statusColors: Record<PolicyData['status'], 'success' | 'warning' | 'error'> = {
  active: 'success',
  pending: 'warning',
  expired: 'error',
};

/** Render basic policy data rows (policyNumber, holder, type, premium). When selectable, includes row id for selection tracking. */
function BasicPolicyRows({ policies, selectable }: Readonly<{ policies: readonly PolicyData[]; selectable?: boolean }>) {
  return (
    <>
      {policies.map((policy) => (
        <Table.Row key={policy.id} {...(selectable ? { id: policy.id } : {})}>
          <Table.Cell>{policy.policyNumber}</Table.Cell>
          <Table.Cell>{policy.holder}</Table.Cell>
          <Table.Cell>{policy.type}</Table.Cell>
          <Table.Cell align="right">{policy.premium}</Table.Cell>
        </Table.Row>
      ))}
    </>
  );
}

function TableShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [sortField, setSortField] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(null);

  const handleSort = (field: string) => {
    if (sortField === field) {
      /* v8 ignore start */
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      } else {
        setSortDirection('asc');
      }
      /* v8 ignore stop */
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPolicies = React.useMemo(() => {
    if (!sortField || !sortDirection) return samplePolicies;

    return [...samplePolicies].sort((a, b) => {
      const aVal = a[sortField as keyof PolicyData];
      const bVal = b[sortField as keyof PolicyData];
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [sortField, sortDirection]);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="table">
          <Table
            size={playgroundProps.size as 'default' | 'compact'}
            selectable={playgroundProps.selectable as boolean}
            stickyHeader={playgroundProps.stickyHeader as boolean}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
          >
            <Table.Head>
              <Table.Row>
                {(playgroundProps.selectable as boolean) && <Table.CheckboxHeaderCell />}
                <Table.HeaderCell>Policy Number</Table.HeaderCell>
                <Table.HeaderCell>Holder</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell align="right">Premium</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <BasicPolicyRows policies={samplePolicies.slice(0, 3)} selectable />
            </Table.Body>
          </Table>
      </ShowcasePreviewSection>

      {/* Basic Table */}
      <ShowcaseDemoSection
        title="Basic Table"
        description="Simple table with headers and data rows"
       
      >
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Policy Number</Table.HeaderCell>
                <Table.HeaderCell>Holder</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell align="right">Premium</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <BasicPolicyRows policies={samplePolicies.slice(0, 3)} />
            </Table.Body>
          </Table>
      </ShowcaseDemoSection>

      {/* With Sorting */}
      <ShowcaseDemoSection
        title="With Sorting"
        description="Sortable column headers with visual indicators"
       
      >
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell
                  sortable
                  sortDirection={sortField === 'policyNumber' ? sortDirection : null}
                  onSort={() => handleSort('policyNumber')}
                >
                  Policy Number
                </Table.HeaderCell>
                <Table.HeaderCell
                  sortable
                  sortDirection={sortField === 'holder' ? sortDirection : null}
                  onSort={() => handleSort('holder')}
                >
                  Holder
                </Table.HeaderCell>
                <Table.HeaderCell
                  sortable
                  sortDirection={sortField === 'type' ? sortDirection : null}
                  onSort={() => handleSort('type')}
                >
                  Type
                </Table.HeaderCell>
                <Table.HeaderCell
                  align="right"
                  sortable
                  sortDirection={sortField === 'premium' ? sortDirection : null}
                  onSort={() => handleSort('premium')}
                >
                  Premium
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <BasicPolicyRows policies={sortedPolicies} />
            </Table.Body>
          </Table>
      </ShowcaseDemoSection>

      {/* Selectable Rows */}
      <ShowcaseDemoSection
        title="Selectable Rows"
        description="Table with row selection using checkboxes"
       
      >
          <VStack gap={3}>
            <HStack gap={2} align="center">
              <Text size="sm" color="secondary">
                Selected: {selectedRows.length} of {samplePolicies.length}
              </Text>
              {selectedRows.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setSelectedRows([])}>
                  Clear Selection
                </Button>
              )}
            </HStack>
            <Table
              selectable
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
            >
              <Table.Head>
                <Table.Row>
                  <Table.CheckboxHeaderCell />
                  <Table.HeaderCell>Policy Number</Table.HeaderCell>
                  <Table.HeaderCell>Holder</Table.HeaderCell>
                  <Table.HeaderCell>Type</Table.HeaderCell>
                  <Table.HeaderCell align="right">Premium</Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                <BasicPolicyRows policies={samplePolicies} selectable />
              </Table.Body>
            </Table>
          </VStack>
      </ShowcaseDemoSection>

      {/* Compact Size */}
      <ShowcaseDemoSection
        title="Compact Size"
        description="Compact table with reduced row height (44px vs 52px)"
       
      >
          <Table size="compact">
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Policy Number</Table.HeaderCell>
                <Table.HeaderCell>Holder</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell align="right">Premium</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <BasicPolicyRows policies={samplePolicies} />
            </Table.Body>
          </Table>
      </ShowcaseDemoSection>

      {/* Custom Cell Rendering */}
      <ShowcaseDemoSection
        title="Custom Cell Rendering"
        description="Table cells with custom content like badges, icons, and formatted data"
       
      >
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell width={180}>Policy Number</Table.HeaderCell>
                <Table.HeaderCell>Holder</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell align="center" width={100}>Status</Table.HeaderCell>
                <Table.HeaderCell align="right" width={120}>Premium</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {samplePolicies.map((policy) => (
                <Table.Row key={policy.id}>
                  <Table.Cell>
                    <HStack gap={2} align="center">
                      <Icon icon={Shield} size="sm" className="text-[#486581]" />
                      <Text size="sm" weight="medium">{policy.policyNumber}</Text>
                    </HStack>
                  </Table.Cell>
                  <Table.Cell>
                    <VStack gap={0}>
                      <Text size="sm">{policy.holder}</Text>
                      <Text size="xs" color="muted">Since {policy.startDate}</Text>
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="sm">{policy.type}</Text>
                  </Table.Cell>
                  <Table.Cell align="center">
                    <Badge variant={statusColors[policy.status]}>
                      {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell align="right">
                    <Text size="sm" weight="medium">{policy.premium}</Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
      </ShowcaseDemoSection>

      {/* Real-world Policy Table */}
      <ShowcaseDemoSection
        title="Real-world Policy Table"
        description="Complete policy management table with all features"
       
      >
          <VStack gap={4}>
            <HStack justify="between" align="center" className="w-full">
              <Text size="lg" weight="semibold">Policy Portfolio</Text>
              <HStack gap={2}>
                <Button variant="secondary" size="sm" leftIcon={<Icon icon={FileText} size="sm" />}>
                  Export
                </Button>
                <Button size="sm" leftIcon={<Icon icon={Shield} size="sm" />}>
                  New Policy
                </Button>
              </HStack>
            </HStack>
            <Table selectable selectedRows={selectedRows} onSelectionChange={setSelectedRows}>
              <Table.Head>
                <Table.Row>
                  <Table.CheckboxHeaderCell />
                  <Table.HeaderCell sortable sortDirection={sortField === 'policyNumber' ? sortDirection : null} onSort={() => handleSort('policyNumber')}>
                    <HStack gap={1} align="center">
                      <Icon icon={FileText} size="sm" />
                      <span>Policy</span>
                    </HStack>
                  </Table.HeaderCell>
                  <Table.HeaderCell sortable sortDirection={sortField === 'holder' ? sortDirection : null} onSort={() => handleSort('holder')}>
                    <HStack gap={1} align="center">
                      <Icon icon={User} size="sm" />
                      <span>Holder</span>
                    </HStack>
                  </Table.HeaderCell>
                  <Table.HeaderCell>Coverage</Table.HeaderCell>
                  <Table.HeaderCell align="center">Status</Table.HeaderCell>
                  <Table.HeaderCell sortable sortDirection={sortField === 'startDate' ? sortDirection : null} onSort={() => handleSort('startDate')}>
                    <HStack gap={1} align="center">
                      <Icon icon={Calendar} size="sm" />
                      <span>Start Date</span>
                    </HStack>
                  </Table.HeaderCell>
                  <Table.HeaderCell align="right" sortable sortDirection={sortField === 'premium' ? sortDirection : null} onSort={() => handleSort('premium')}>
                    <HStack gap={1} align="center" justify="end">
                      <Icon icon={DollarSign} size="sm" />
                      <span>Premium</span>
                    </HStack>
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {sortedPolicies.map((policy) => (
                  <Table.Row key={policy.id} id={policy.id}>
                    <Table.Cell>
                      <Text size="sm" weight="medium" className="text-[#334E68]">
                        {policy.policyNumber}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="sm">{policy.holder}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="sm">{policy.type}</Text>
                    </Table.Cell>
                    <Table.Cell align="center">
                      <Badge variant={statusColors[policy.status]} size="sm">
                        {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="sm" color="secondary">{policy.startDate}</Text>
                    </Table.Cell>
                    <Table.Cell align="right">
                      <Text size="sm" weight="medium">{policy.premium}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {selectedRows.length > 0 && (
              <HStack gap={2} className="pt-2 border-t border-[#E8E6E3]">
                <Text size="sm" color="secondary">{selectedRows.length} selected</Text>
                <Button size="sm" variant="ghost">Bulk Edit</Button>
                <Button size="sm" variant="danger">Delete Selected</Button>
              </HStack>
            )}
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const TableShowcase: ShowcaseDefinition = {
  id: 'table',
  name: 'Table',
  category: 'data',
  description: 'Data table with sorting, selection, and customization',
  propsDefinition: [
    {
      name: 'selectable',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether rows can be selected with checkboxes',
    },
    {
      name: 'size',
      type: 'select',
      options: ['default', 'compact'],
      defaultValue: 'default',
      description: 'Row height variant (default=52px, compact=44px)',
    },
    {
      name: 'stickyHeader',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the header sticks to the top when scrolling',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="table" (implicit on table element)',
      'scope="col" on header cells',
      'aria-sort on sortable columns',
      'aria-selected on selectable rows',
    ],
    keyboardNav: [
      'Tab: Move focus through cells',
      'Enter/Space: Activate sortable headers',
      'Space: Toggle row selection',
    ],
    notes: [
      'Header cells properly associated with data cells',
      'Sort direction announced via aria-sort',
      'Checkbox labels include row context',
      'Focus indicators visible on interactive elements',
    ],
  },
  component: TableShowcaseComponent,
};
